import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Check, ArrowRight, Phone, Globe, MapPin, Star, Loader2, Zap, BarChart3 } from 'lucide-react';
import { toast } from 'sonner';
import { useBusinessAuth } from '@/context/BusinessAuthContext';

const API_URL = '';
const LOGO_WORDMARK = "/brand/disqover-wordmark-dark.png";

const CORE_FEATURES = [
  { text: 'Phone contact button', icon: Phone },
  { text: 'Website link', icon: Globe },
  { text: 'Map directions', icon: MapPin },
  { text: 'Full portal access', icon: Check },
];

const BOOST_FEATURES = [
  { text: 'Featured badge', icon: Star },
  { text: 'Priority placement', icon: Zap },
  { text: 'Enhanced map visibility', icon: MapPin },
];

export default function PartnerLandingPage() {
  const navigate = useNavigate();
  const { user, loading: authLoading, subscription, business, requestMagicLink } = useBusinessAuth();
  const [email, setEmail] = useState('');
  const [includeFeaturedBoost, setIncludeFeaturedBoost] = useState(false);
  const [loading, setLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [debugToken, setDebugToken] = useState(null);
  const [emailDelivered, setEmailDelivered] = useState(false);

  const corePrice = 199;
  const boostPrice = 99;
  const totalPrice = corePrice + (includeFeaturedBoost ? boostPrice : 0);

  // Redirect authenticated users with active subscription to portal
  useEffect(() => {
    if (!authLoading && user) {
      // User is logged in - check if they have an active subscription or business
      if (subscription?.status === 'active' || business) {
        navigate('/portal');
      }
    }
  }, [user, authLoading, subscription, business, navigate]);

  const handleGetStarted = async (e) => {
    e.preventDefault();
    
    if (!email) {
      toast.error('Please enter your email');
      return;
    }

    setLoading(true);
    try {
      const result = await requestMagicLink(email);
      setEmailSent(true);
      setEmailDelivered(result.email_sent);
      if (result.debug_token) {
        setDebugToken(result.debug_token);
      }
      toast.success(result.email_sent 
        ? 'Magic link sent! Check your email.' 
        : 'Check below for your login link.'
      );
    } catch (error) {
      toast.error('Failed to send magic link');
    } finally {
      setLoading(false);
    }
  };

  const initiateCheckout = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/partner/checkout/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          include_featured_boost: includeFeaturedBoost,
          origin_url: window.location.origin
        })
      });

      if (!response.ok) {
        throw new Error('Failed to create checkout');
      }

      const data = await response.json();
      window.location.href = data.url;
    } catch (error) {
      toast.error('Failed to start checkout');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white" data-testid="partner-landing-page">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-xl border-b border-slate-100">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src={LOGO_WORDMARK} alt="Disqover" className="h-8 w-auto" />
          </Link>
          
          <div className="flex items-center gap-4">
            {user ? (
              <>
                <span className="text-sm text-slate-600">{user.email}</span>
                <Link to="/portal">
                  <Button variant="outline" className="rounded-full">
                    My Portal
                  </Button>
                </Link>
              </>
            ) : (
              <Link to="/partner/login">
                <Button variant="ghost" className="text-slate-600">
                  Sign In
                </Button>
              </Link>
            )}
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="pt-32 pb-16 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-teal-50 text-teal-700 rounded-full text-sm font-medium mb-6">
            <Star className="w-4 h-4" />
            Join Gold Coast businesses on Disqover
          </div>
          
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 font-heading leading-tight mb-6">
            Get discovered by
            <span className="text-teal-600"> local visitors</span>
          </h1>
          
          <p className="text-lg sm:text-xl text-slate-600 max-w-2xl mx-auto mb-10">
            List your business on Disqover and reach tourists and locals looking for the best experiences on the Gold Coast.
          </p>

          {!user ? (
            emailSent ? (
              <div className="max-w-md mx-auto bg-teal-50 rounded-2xl p-6 text-center">
                <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Check className="w-6 h-6 text-teal-600" />
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2">Check your email</h3>
                <p className="text-slate-600 mb-4">
                  We&apos;ve sent a magic link to <strong>{email}</strong>
                </p>
                {!emailDelivered && debugToken && (
                  <div className="mt-4 p-4 bg-white rounded-xl border border-slate-200">
                    <p className="text-xs text-slate-500 mb-2">Testing: Click to verify</p>
                    <Link 
                      to={`/partner/verify?token=${debugToken}`}
                      className="text-teal-600 text-sm font-medium hover:underline"
                    >
                      Verify Email →
                    </Link>
                  </div>
                )}
              </div>
            ) : (
              <form onSubmit={handleGetStarted} className="max-w-md mx-auto">
                <div className="flex gap-3">
                  <Input
                    type="email"
                    placeholder="Enter your business email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="h-14 rounded-full px-6 text-base"
                    data-testid="email-input"
                  />
                  <Button 
                    type="submit" 
                    disabled={loading}
                    className="h-14 px-8 rounded-full bg-teal-600 hover:bg-teal-700 text-base font-medium"
                    data-testid="get-started-btn"
                  >
                    {loading ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <>
                        Get Started
                        <ArrowRight className="w-5 h-5 ml-2" />
                      </>
                    )}
                  </Button>
                </div>
                
                {/* View Demo Link */}
                <div className="mt-4">
                  <Link 
                    to="/analytics-demo" 
                    className="inline-flex items-center gap-2 text-slate-500 hover:text-teal-600 transition-colors text-sm"
                    data-testid="view-demo-link"
                  >
                    <BarChart3 className="w-4 h-4" />
                    <span>Preview your analytics dashboard</span>
                    <ArrowRight className="w-3 h-3" />
                  </Link>
                </div>
              </form>
            )
          ) : (
            <div className="max-w-md mx-auto text-center">
              <div className="bg-teal-50 rounded-2xl p-6 mb-4">
                <p className="text-sm text-teal-700 mb-1">Signed in as</p>
                <p className="font-semibold text-slate-900">{user.email}</p>
              </div>
              <p className="text-slate-600 mb-4">Configure your plan below and subscribe</p>
              <a href="#pricing" className="text-teal-600 font-medium hover:underline">
                View pricing ↓
              </a>
            </div>
          )}
        </div>
      </section>

      {/* Pricing */}
      <section className="py-16 px-6 bg-slate-50" id="pricing">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 font-heading mb-4">
              Simple, transparent pricing
            </h2>
            <p className="text-lg text-slate-600">
              One plan. Optional boost. No hidden fees.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 max-w-3xl mx-auto">
            {/* Core Listing */}
            <div className="bg-white rounded-3xl p-8 border-2 border-teal-500 shadow-lg">
              <div className="flex items-center gap-2 mb-2">
                <span className="px-3 py-1 bg-teal-100 text-teal-700 text-xs font-semibold rounded-full">
                  REQUIRED
                </span>
              </div>
              
              <h3 className="text-2xl font-bold text-slate-900 font-heading mb-2">Core Listing</h3>
              <p className="text-slate-500 text-sm mb-4">Everything you need to reach local visitors</p>

              <div className="mb-6">
                <span className="text-4xl font-bold text-slate-900">${corePrice}</span>
                <span className="text-slate-500">/month</span>
              </div>

              <ul className="space-y-3 mb-6">
                {CORE_FEATURES.map((feature) => (
                  <li key={feature.text} className="flex items-center gap-3">
                    <div className="w-5 h-5 rounded-full bg-teal-100 text-teal-600 flex items-center justify-center">
                      <Check className="w-3 h-3" />
                    </div>
                    <span className="text-slate-700">{feature.text}</span>
                  </li>
                ))}
              </ul>

              <div className="pt-4 border-t border-slate-100">
                <p className="text-xs text-slate-500 text-center">
                  Includes all contact options and standard placement
                </p>
              </div>
            </div>

            {/* Featured Boost */}
            <div 
              className={`bg-white rounded-3xl p-8 border-2 transition-all cursor-pointer ${
                includeFeaturedBoost 
                  ? 'border-amber-500 shadow-lg' 
                  : 'border-slate-200 hover:border-slate-300'
              }`}
              onClick={() => user && setIncludeFeaturedBoost(!includeFeaturedBoost)}
              data-testid="featured-boost-card"
            >
              <div className="flex items-center gap-2 mb-2">
                <span className="px-3 py-1 bg-amber-100 text-amber-700 text-xs font-semibold rounded-full">
                  OPTIONAL ADD-ON
                </span>
              </div>
              
              <h3 className="text-2xl font-bold text-slate-900 font-heading mb-2">Featured Boost</h3>
              <p className="text-slate-500 text-sm mb-4">Stand out from the crowd</p>

              <div className="mb-6">
                <span className="text-4xl font-bold text-slate-900">+${boostPrice}</span>
                <span className="text-slate-500">/month</span>
              </div>

              <ul className="space-y-3 mb-6">
                {BOOST_FEATURES.map((feature) => (
                  <li key={feature.text} className="flex items-center gap-3">
                    <div className={`w-5 h-5 rounded-full flex items-center justify-center ${
                      includeFeaturedBoost ? 'bg-amber-100 text-amber-600' : 'bg-slate-100 text-slate-400'
                    }`}>
                      <feature.icon className="w-3 h-3" />
                    </div>
                    <span className={includeFeaturedBoost ? 'text-slate-700' : 'text-slate-500'}>
                      {feature.text}
                    </span>
                  </li>
                ))}
              </ul>

              {user && (
                <div className="pt-4 border-t border-slate-100">
                  <label className="flex items-center justify-center gap-2 cursor-pointer">
                    <input 
                      type="checkbox" 
                      checked={includeFeaturedBoost}
                      onChange={(e) => setIncludeFeaturedBoost(e.target.checked)}
                      className="w-5 h-5 rounded border-slate-300 text-amber-500 focus:ring-amber-500"
                    />
                    <span className="text-sm font-medium text-slate-700">
                      Add Featured Boost
                    </span>
                  </label>
                </div>
              )}
            </div>
          </div>

          {/* Total & CTA */}
          {user && (
            <div className="mt-8 max-w-md mx-auto">
              <div className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm mb-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-slate-600">Core Listing</span>
                  <span className="font-semibold text-slate-900">${corePrice}/mo</span>
                </div>
                {includeFeaturedBoost && (
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-slate-600">Featured Boost</span>
                    <span className="font-semibold text-slate-900">+${boostPrice}/mo</span>
                  </div>
                )}
                <div className="pt-3 mt-3 border-t border-slate-100 flex items-center justify-between">
                  <span className="font-semibold text-slate-900">Total</span>
                  <span className="text-2xl font-bold text-teal-600">${totalPrice}/mo</span>
                </div>
              </div>

              <Button 
                onClick={initiateCheckout}
                disabled={loading}
                className="w-full h-14 rounded-full bg-teal-600 hover:bg-teal-700 text-lg font-medium"
                data-testid="subscribe-btn"
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    Subscribe Now
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </>
                )}
              </Button>
            </div>
          )}
        </div>
      </section>

      {/* How it Works */}
      <section className="py-16 px-6">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 font-heading text-center mb-12">
            How it works
          </h2>

          <div className="grid sm:grid-cols-3 gap-8">
            {[
              { step: '1', title: 'Sign up', desc: 'Enter your email and subscribe' },
              { step: '2', title: 'Add your listing', desc: 'Fill in your business details' },
              { step: '3', title: 'Get discovered', desc: 'Start reaching visitors instantly' }
            ].map((item) => (
              <div key={item.step} className="text-center">
                <div className="w-12 h-12 bg-teal-100 text-teal-600 rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-4">
                  {item.step}
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2">{item.title}</h3>
                <p className="text-slate-600">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 border-t border-slate-100">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <img src={LOGO_WORDMARK} alt="Disqover" className="h-6 w-auto" />
            <span className="text-slate-400">|</span>
            <span className="text-slate-500 text-sm">tourism.upgraded</span>
          </div>
          
          <div className="flex items-center gap-6 text-sm text-slate-500">
            <Link to="/" className="hover:text-slate-700">Home</Link>
            <Link to="/info" className="hover:text-slate-700">About</Link>
            <a href="mailto:hello@disqover.com.au" className="hover:text-slate-700">Contact</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
